import { Reveal } from "../components/Reveal";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { RestaurantCarousel } from "../components/RestaurantCarousel";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Clock, Phone, Mail, ChevronDown } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

const HERO_IMG = "/hero-caravelle.webp";
const RESTO_IMG = "/carte-restaurant.webp";
const PETITDEJ_IMG = "/petit-dejeuner.webp";
const GOURMANDE_IMG = "/cocktail-1.webp";
const SECONDARY_IMG = "/fruits-de-mer.webp";

export default function Accueil() {
  const { t } = useLang();

  const cards = [
    { to: "/carte-restaurant", title: t.nav.restaurant, sub: t.cardSubs.restaurant, img: RESTO_IMG, testid: "card-restaurant" },
    { to: "/carte-petit-dejeuner", title: t.nav.petitDejeuner, sub: t.cardSubs.petitDejeuner, img: PETITDEJ_IMG, testid: "card-petitdejeuner" },
    { to: "/carte-gourmande", title: t.nav.gourmande, sub: t.cardSubs.gourmande, img: GOURMANDE_IMG, testid: "card-gourmande" },
  ];

  return (
    <div data-testid="page-accueil" className="page-in" style={{ background: "var(--color-bg)" }}>
      <Header />

      {/* HERO */}
      <section data-testid="hero" className="relative min-h-screen flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="La Caravelle, Narbonne" className="w-full h-full object-cover slow-zoom" />
          <div
            className="absolute inset-0"
            style={{ background: "linear-gradient(180deg, rgba(44,30,22,0.10) 0%, rgba(44,30,22,0.05) 40%, rgba(44,30,22,0.80) 100%)" }}
          />
        </div>

        <div className="absolute top-24 left-0 right-0 overflow-hidden opacity-80 select-none pointer-events-none">
          <div className="marquee-track text-[color:var(--color-bg)]/70 text-xs tracking-[0.4em] uppercase">
            {Array.from({ length: 2 }).map((_, k) => (
              <div key={k} className="flex gap-12 px-6 whitespace-nowrap">
                {t.common.marquee.map((m, idx) => (
                  <span key={idx}>{m}{idx < t.common.marquee.length - 1 ? " ·" : ""}</span>
                ))}
                <span>·</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-8 pb-20 sm:pb-32">
          <div className="max-w-4xl">
            <p className="text-[11px] tracking-[0.4em] uppercase mb-6" style={{ color: "rgba(246,244,240,0.85)" }}>
              {t.common.heroOverline}
            </p>
            <h1
              className="font-[\'Playfair_Display\'] text-5xl sm:text-7xl md:text-[7.5rem] leading-[0.95] tracking-tight"
              style={{ color: "var(--color-bg)" }}
            >
              {t.common.heroTitle}
              <br />
              <span className="italic font-normal opacity-90">{t.common.heroTitleItalic}</span>
            </h1>
            <p className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed" style={{ color: "rgba(246,244,240,0.85)" }}>
              {t.common.heroSub}
            </p>

            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/carte-restaurant" data-testid="hero-cta-restaurant" className="btn-pill btn-light">
                {t.common.discoverCard} <ArrowRight size={16} />
              </Link>
              <a
                href="tel:0468325017"
                data-testid="hero-cta-phone"
                className="btn-pill"
                style={{ background: "transparent", color: "var(--color-bg)", border: "1px solid rgba(246,244,240,0.4)" }}
              >
                <Phone size={15} /> {t.common.reserve}
              </a>
            </div>
          </div>
        </div>

        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center text-[color:var(--color-bg)]/70 text-xs tracking-[0.3em]">
          <span>{t.common.scroll}</span>
          <ChevronDown size={14} className="mt-1 animate-bounce" />
        </div>
      </section>

      {/* INTRO */}
      <section className="relative py-24 sm:py-32" style={{ background: "var(--color-bg)" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <Reveal className="lg:col-span-5">
            <p className="text-[11px] tracking-[0.3em] uppercase mb-6" style={{ color: "var(--color-text-muted)" }}>
              {t.common.house}
            </p>
            <h2
              className="text-4xl sm:text-5xl md:text-6xl leading-tight font-[\'Playfair_Display\']"
              style={{ color: "var(--color-dark)" }}
            >
              {t.common.houseTitle}
              <br />
              <span className="italic opacity-80">{t.common.houseTitleItalic}</span>
            </h2>
          </Reveal>
          <Reveal className="lg:col-span-7 lg:pt-6" delay={120}>
            <p className="text-lg leading-relaxed" style={{ color: "var(--color-text-muted)" }}>
              {t.common.houseP1}
            </p>
            <p className="mt-6 text-lg leading-relaxed" style={{ color: "var(--color-text-muted)" }}>
              {t.common.houseP2}
            </p>
          </Reveal>
        </div>
      </section>

      {/* CARTES */}
      <section
        data-testid="cartes-section"
        className="relative py-24 sm:py-32"
        style={{ background: "var(--color-bg-secondary)" }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal className="mb-16 max-w-2xl">
            <p className="text-[11px] tracking-[0.3em] uppercase mb-4" style={{ color: "var(--color-text-muted)" }}>
              {t.common.ourCards}
            </p>
            <h2
              className="text-4xl sm:text-5xl md:text-6xl font-[\'Playfair_Display\']"
              style={{ color: "var(--color-dark)" }}
            >
              {t.common.ourCardsTitle}{" "}
              <span className="italic opacity-80">{t.common.ourCardsTitleItalic}</span>
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {cards.map((c, idx) => (
              <Reveal key={c.to} delay={idx * 120}>
                <Link
                  to={c.to}
                  data-testid={c.testid}
                  className="group block relative overflow-hidden rounded-3xl aspect-[3/4] transition-all duration-500 hover:-translate-y-1"
                >
                  <img
                    src={c.img}
                    alt={c.title}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-110"
                  />
                  <div
                    className="absolute inset-0"
                    style={{ background: "linear-gradient(180deg, rgba(44,30,22,0.1) 0%, rgba(44,30,22,0.85) 100%)" }}
                  />
                  <div className="absolute top-5 left-5 right-5 flex justify-between items-start">
                    <span className="text-[10px] tracking-[0.35em] uppercase text-[color:var(--color-bg)]/80">
                      0{idx + 1}
                    </span>
                    <span className="h-9 w-9 rounded-full glass-light flex items-center justify-center transition-transform duration-500 group-hover:rotate-45">
                      <ArrowRight size={14} color="var(--color-dark)" />
                    </span>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-7">
                    <h3 className="text-2xl sm:text-3xl font-[\'Playfair_Display\']" style={{ color: "var(--color-bg)" }}>
                      {c.title}
                    </h3>
                    <p className="mt-2 text-sm" style={{ color: "rgba(246,244,240,0.8)" }}>
                      {c.sub}
                    </p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CARROUSEL RESTAURANT */}
      <RestaurantCarousel />

      {/* SIGNATURE */}
      <section className="relative py-24 sm:py-32" style={{ background: "var(--color-bg)" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <Reveal className="relative">
            <div className="aspect-[4/5] overflow-hidden rounded-3xl">
              <img
                src={SECONDARY_IMG}
                alt="Plat signature La Caravelle"
                className="w-full h-full object-cover transition-transform duration-[1500ms] hover:scale-105"
              />
            </div>
          </Reveal>

          <Reveal delay={120}>
            <p className="text-[11px] tracking-[0.3em] uppercase mb-6" style={{ color: "var(--color-text-muted)" }}>
              {t.common.signatures}
            </p>
            <h2
              className="text-4xl sm:text-5xl font-[\'Playfair_Display\'] leading-tight"
              style={{ color: "var(--color-dark)" }}
            >
              {t.common.signaturesTitle}
              <br />
              <span className="italic opacity-80">{t.common.signaturesTitleItalic}</span>
            </h2>
            <ul className="mt-10 space-y-5 text-base" style={{ color: "var(--color-text-muted)" }}>
              {[
                ["Ceviche de crevettes", "14 €"],
                ["Tataki de thon", "14 €"],
                ["Brochettes poulpe & gambas grillées", "24 €"],
                ["Mi-cuit chocolat noir, cœur coulant", "8 €"],
              ].map(([n, p]) => (
                <li key={n} className="flex items-baseline gap-4">
                  <span className="font-[\'Playfair_Display\'] text-xl text-[color:var(--color-dark)]">{n}</span>
                  <span className="flex-1 border-b border-dotted border-[color:var(--color-dark)]/40 translate-y-[-4px]" />
                  <span className="font-[\'Playfair_Display\'] text-xl text-[color:var(--color-accent)]">{p}</span>
                </li>
              ))}
            </ul>

            <Link to="/carte-restaurant" data-testid="signature-cta" className="mt-10 inline-flex btn-pill btn-dark">
              {t.common.seeFullCard} <ArrowRight size={15} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* INFOS */}
      <section
        data-testid="infos-pratiques"
        className="relative overflow-hidden"
        style={{ background: "var(--color-dark)", color: "var(--color-bg)" }}
      >
        <div className="absolute inset-0 grain pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <Reveal>
            <p className="text-[11px] tracking-[0.3em] uppercase opacity-60 mb-4">{t.common.visitUs}</p>
            <h2 className="text-4xl sm:text-5xl font-[\'Playfair_Display\']">
              {t.common.visitTitle} <span className="italic opacity-80">{t.common.visitTitleItalic}</span>
            </h2>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: <MapPin size={18} />, label: t.common.address, lines: ["Prom. du Front de Mer", "11100 Narbonne"] },
              { icon: <Clock size={18} />, label: t.common.hours, lines: [t.common.everyday, "9h00 – 00h00"] },
              {
                icon: <Phone size={18} />,
                label: t.common.reservations,
                lines: ["04 68 32 50 17", "lacaravellenarbonne@gmail.com"],
                actions: true,
              },
            ].map((b, i) => (
              <Reveal key={b.label} delay={i * 100}>
                <div className="glass-dark rounded-3xl p-8 h-full">
                  <div className="flex items-center gap-3 opacity-80">
                    {b.icon}
                    <p className="text-[10px] tracking-[0.3em] uppercase">{b.label}</p>
                  </div>
                  <div className="mt-6 space-y-1 font-[\'Playfair_Display\'] text-xl">
                    {b.lines.map((l) => (
                      <p key={l}>{l}</p>
                    ))}
                  </div>
                  {b.actions && (
                    <div className="mt-6 flex flex-wrap gap-2">
                      <a href="tel:0468325017" data-testid="infos-call" className="btn-pill btn-light text-xs">
                        <Phone size={13} /> {t.common.call}
                      </a>
                      <a
                        href="mailto:lacaravellenarbonne@gmail.com"
                        data-testid="infos-email"
                        className="btn-pill text-xs"
                        style={{
                          background: "transparent",
                          color: "var(--color-bg)",
                          border: "1px solid rgba(246,244,240,0.3)",
                        }}
                      >
                        <Mail size={13} /> {t.common.write}
                      </a>
                    </div>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
